import React, { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, File, ImagePlus } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/lib/supabaseClient";
import { validateFile, getAllowedFileTypesDescription } from "@/lib/fileValidation";

interface DocumentUploaderProps {
  entityType: "claim" | "contract";
  entityId: string;
  onSuccess?: () => void;
}

export default function DocumentUploader({ entityType, entityId, onSuccess }: DocumentUploaderProps) {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [documentType, setDocumentType] = useState(
    entityType === "claim" ? "Claim" : ""
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  
  // Document type options based on entity type
  const documentTypeOptions = entityType === "claim" 
    ? [
        { value: "Claim", label: "Claim" },
      ]
    : [
        { value: "Contract", label: "Contract" },
        { value: "Compliance", label: "Compliance" },
        { value: "Other", label: "Other" },
      ];
  
  // Reset form
  const resetForm = () => {
    setSelectedFile(null);
    setDocumentType(entityType === "claim" ? "Claim" : "");
    setProgress(0);
    setIsDragOver(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  // Handle file selection with validation
  const handleFileSelection = (file: File) => {
    const validation = validateFile(file);
    if (!validation.isValid) {
      toast({
        title: "Invalid file",
        description: validation.error,
        variant: "destructive",
      });
      return;
    }
    setSelectedFile(file);
  };
  
  // Handle file input change
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelection(files[0]);
    }
  };
  
  // Handle drag and drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };
  
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };
  
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelection(files[0]);
    }
  };
  
  // Trigger file input click
  const handleUploadAreaClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  // Upload document
  const handleUpload = async () => {
    if (!selectedFile || !documentType) {
      toast({
        title: "Missing information",
        description: "Please select a file and document type",
        variant: "destructive",
      });
      return;
    }
    
    setUploading(true);
    setProgress(10);
    
    try {
      if (entityType === "claim") {
        // For claims, use the direct claims API endpoint
        setProgress(20);
        
        const metadataResponse = await apiRequest("POST", `/api/claims/${entityId}/documents`, {
          fileName: selectedFile.name,
          documentType: documentType,
          fileSize: selectedFile.size,
          contentType: selectedFile.type || 'application/octet-stream',
        });
        
        const documentData = await metadataResponse.json();
        
        setProgress(50);
        
        // Upload the actual file to Supabase storage using the path returned by the API
        const uploadPath = documentData.uploadPath;
        
        const { error: uploadError } = await supabase.storage
          .from('documents')
          .upload(uploadPath, selectedFile, {
            cacheControl: '3600',
            upsert: false
          });
        
        if (uploadError) {
          console.error('Supabase storage upload error:', uploadError);
          throw new Error(`File upload failed: ${uploadError.message}`);
        }
        
        setProgress(100);
        
        // Success
        toast({
          title: "Document uploaded",
          description: "The document has been uploaded successfully",
        });
        
        // Invalidate queries to update UI
        queryClient.invalidateQueries({ queryKey: [`/api/claims/${entityId}`] });
        
        // Reset and close
        setIsOpen(false);
        resetForm();
        
        // Callback
        onSuccess?.();
      } else {
        // For contracts (projects), use the direct projects API endpoint
        setProgress(20);
          
        const metadataResponse = await apiRequest("POST", `/api/projects/${entityId}/documents`, {
              fileName: selectedFile.name,
          documentType: documentType,
              fileSize: selectedFile.size,
          contentType: selectedFile.type || 'application/octet-stream',
        });
        
        const documentData = await metadataResponse.json();
        
        setProgress(50);
        
        // Upload the actual file to Supabase storage using the path returned by the API
        const uploadPath = documentData.uploadPath;
        
        const { error: uploadError } = await supabase.storage
          .from('documents')
          .upload(uploadPath, selectedFile, {
            cacheControl: '3600',
            upsert: false
          });
        
        if (uploadError) {
          console.error('Supabase storage upload error:', uploadError);
          throw new Error(`File upload failed: ${uploadError.message}`);
        }
            
            setProgress(100);
            
            // Success
            toast({
              title: "Document uploaded",
              description: "The document has been uploaded successfully",
            });
            
            // Invalidate queries to update UI
        queryClient.invalidateQueries({ queryKey: [`/api/projects/${entityId}/documents`] });
        queryClient.invalidateQueries({ queryKey: [`/api/projects/${entityId}/documents/analysis`] });
            
            // Reset and close
            setIsOpen(false);
            resetForm();
            
            // Callback
            onSuccess?.();
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast({
        title: "Upload failed",
        description: error instanceof Error ? error.message : "Failed to upload document",
        variant: "destructive",
      });
    } finally {
      setTimeout(() => {
        setUploading(false);
        setProgress(0);
      }, 1000);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      setIsOpen(open);
      if (!open) {
        resetForm();
      }
    }}>
      <DialogTrigger asChild>
        <Button>
          <Upload className="mr-2 h-4 w-4" />
          Upload Document
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Upload Document</DialogTitle>
          <DialogDescription>
            Upload a document to attach to this {entityType}.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="documentType">Document Type</Label>
            <Select
              value={documentType}
              onValueChange={setDocumentType}
            >
              <SelectTrigger id="documentType">
                <SelectValue placeholder="Select document type" />
              </SelectTrigger>
              <SelectContent>
                {documentTypeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label>File</Label>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.jpg,.jpeg,.png,.gif,.bmp,.webp"
              onChange={handleFileChange}
            />
            <div 
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                isDragOver 
                  ? 'border-primary bg-primary/10' 
                  : 'border-muted-foreground/25 hover:bg-muted/50'
              }`}
              onClick={handleUploadAreaClick}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {selectedFile ? (
                <div className="flex items-center justify-center gap-2">
                  <File className="h-8 w-8 text-primary" />
                  <div>
                    <p className="font-medium">{selectedFile.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <ImagePlus className="h-10 w-10 text-muted-foreground mb-2" />
                  <p className="text-muted-foreground">
                    Click to select a file or drag and drop
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {getAllowedFileTypesDescription()}
                  </p>
                </div>
              )}
            </div>
          </div>
          
          {uploading && (
            <div className="space-y-2">
              <Progress value={progress} className="h-2" />
              <p className="text-sm text-muted-foreground text-center">
                Uploading... {progress}%
              </p>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setIsOpen(false)}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!selectedFile || !documentType || uploading}
          >
            {uploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
